<template>
  <q-footer class="bg-white">
    <AeroflotFooterContainer v-if="domain.is.aeroflot" />
    <template v-else>
      <div
        :class="['text-black', {
          'gt-sm': $slots.mobile,
        }]"
      >
        <div class="container q-pa-16">
          <div class="row q-col-gutter-16">
            <div :class="['col-12 column', domain.hasApp ? 'col-md-2' : 'col-md-3']">
              <div class="text-bold q-mb-16">
                <a
                  class="footer-link text-black"
                  :href="`/${$i18n.locale}/services`"
                >
                  {{ $t('footer.services.header') }}
                </a>
              </div>
              <template v-for="(_, navName) in $tm('footer.services')">
                <div
                  v-if="navName !== 'header'"
                  :key="navName"
                >
                  <a
                    :href="`/${$i18n.locale}/services/${navName}`"
                    class="text-black fs-14 footer-link"
                  >
                    {{ $t(`footer.services.${navName}`) }}
                  </a>
                </div>
              </template>
            </div>
            <div class="col-12 column col-md-4 q-ml-auto">
              <div class="text-bold q-mb-16">
                {{ $t('footer.sitemap.header') }}
              </div>
              <div
                v-for="(navLink, navName) in footerNavLinks"
                :key="navName"
              >
                <a
                  class="text-black fs-14 footer-link"
                  :href="navLink.href || navLinkWithLocale(navLink)"
                  @click="navigate($event, navLink)"
                >
                  {{ $t(`header.nav.${navName}`) }}
                </a>
              </div>
              <div
                v-for="link in legalPages"
                :key="link"
              >
                <a
                  :href="`/${$i18n.locale}/${link}`"
                  class="text-black fs-14 footer-link"
                >
                  {{ $t(`footer.sitemap.nav.${link}`) }}
                </a>
              </div>
            </div>
            <div :class="['col-12', domain.hasApp ? 'col-md-4' : 'col-md-5']">
              <div class="text-bold q-mb-16">
                {{ $t('footer.address.header') }}
              </div>
              <FooterAddressInfo />
            </div>
            <div
              v-if="domain.hasApp"
              class="col-12 col-md-2 flex justify-end"
            >
              <div>
                <q-no-ssr>
                  <InlineSvg
                    src="svg/get_the_app.svg"
                    width="122px"
                    with-out-skeleton
                  />
                  <div class="flex bg-white q-mt-16">
                    <QrcodeVue
                      :value="appLinks.qr"
                      :size="141"
                      level="H"
                    />
                  </div>
                  <LinkApps
                    class="q-mt-16 block"
                    type="passenger"
                  />
                </q-no-ssr>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showSecondFooter"
          class="footer-second ios-bottom-padding"
        >
          <q-separator class="container" />
          <div class="container flex justify-between q-pa-16 no-wrap">
            <div
              v-if="!domain.hideTrademark"
              :class="{ 'q-mr-auto': !domain.hasSocialLinks }"
            >
              <div>©KG GLOBAL LIMITED. GetTransfer® is trademark of KG GLOBAL LIMITED.</div>
              <div>All rights reserved.</div>
            </div>
            <div
              v-if="domain.hasSocialLinks"
              class="flex no-wrap q-ml-8"
            >
              <q-btn
                v-for="(value, key) in configs.social_links"
                :key="key"
                :href="value"
                target="_blank"
                flat
                round
              >
                <q-icon
                  :name="`img:common/social/${key}.svg`"
                  :alt="key"
                  size="32px"
                />
              </q-btn>
            </div>
            <div
              v-if="domain.is.uzairways"
              class="flex items-center"
            >
              <span class="q-mr-4">
                {{ $t('label.service_provided_by') }}
              </span>
              <img
                width="170"
                src="common/logo-white.svg?v=2022"
                alt="GetTransfer"
              >
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="$slots.mobile"
        class="lt-md"
      >
        <slot name="mobile" />
      </div>
    </template>
  </q-footer>
</template>

<script setup>
import { useQuasar } from 'quasar';
import { inject, computed } from 'vue';
import QrcodeVue from 'qrcode.vue';
import { storeToRefs } from 'pinia';

import AeroflotFooterContainer from './AeroflotFooterContainer';
import InlineSvg from './InlineSvg';
import LinkApps from './LinkApps';
import FooterAddressInfo from './FooterAddressInfo';

import useAppLinks from '~composables/useAppLinks';
import useNavLinks from '~composables/useNavLinks';
import { useConfigsStore } from '~stores/configs';

const configsStore = useConfigsStore();
const { configs } = storeToRefs(configsStore);

const { appLinks } = useAppLinks('passenger');
const $q = useQuasar();
const { navLinks, navLinkWithLocale, legalPages } = useNavLinks();

const bus = inject('bus');
const domain = inject('domain');

const footerNavLinks = computed(() => {
  const links = { ...navLinks.value };
  // Override support link to use mailto
  if (links.support) {
    links.support = { href: 'mailto:support@transfer-now.ru' };
  }
  return links;
});

function navigate(event, target) {
  if ($q.screen.gt.sm && target === 'support') {
    event.preventDefault();
    bus.emit('show-support');
  }
}

const showSecondFooter = computed(() => (!domain.hideTrademark || domain.hasSocialLinks || domain.is.uzairways));

</script>

<style lang="scss" scoped>
footer :deep(.q-footer) {
  padding-bottom: 0 !important;
}

.footer {
  &-second {
    font-size: 10px;
  }
  &-link {
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  &-item {
    padding: 0;
    min-height: 20px;
  }
}
</style>
